import React, { memo, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { shallowEqual, useSelector } from "react-redux";
import { RootState } from "typedefs";
import styles from "./styles.module.scss";

interface Crumb {
  key: string;
  // Entity names come pre-resolved from state; section/sub-page labels are
  // translation keys resolved at render time.
  label?: string;
  labelKey?: string;
  to?: string;
}

interface EntityRef {
  id?: string;
  name?: string;
}

interface SelectedEntities {
  space?: EntityRef;
  demoSpace?: EntityRef;
  group?: EntityRef;
  channel?: EntityRef;
  template?: EntityRef;
  // Lists carry no id: the segments fetch clears segmentsData while
  // pending, so it can't hold the previous list during navigation.
  list?: string;
}

// Entity fetches don't clear the previously selected entity while pending
// (see e.g. fetchGroupById), so during navigation the store can still hold
// the entity from the page just left. Only trust a name whose entity id
// matches the route; a mismatch counts as missing so the trail truncates
// instead of showing the previous entity's name.
function nameMatchingRoute(
  entity: EntityRef | undefined,
  routeId: string,
): string | undefined {
  return entity?.id != null && String(entity.id) === routeId
    ? entity.name
    : undefined;
}

// /spaces/* segments that render the listing itself rather than a space
// detail (see RouterData.ts); "new" is the creation flow, "demo" the demo
// space detail — neither is a space id.
const LISTING_SPACE_SEGMENTS = ["all", "smartList", "list"];
const RESERVED_SPACE_SEGMENTS = [...LISTING_SPACE_SEGMENTS, "new", "demo"];

// The Spaces listing keeps its filter in the URL (?object=…&name=…&id=…) and
// re-hydrates it on mount, so remembering the last listing URL is enough for
// the Spaces crumb to bring the filter back. sessionStorage so it survives a
// hard reload of a detail page without leaking across tabs.
const SPACES_LISTING_URL_KEY = "breadcrumb.spacesListingUrl";

function segmentsOf(pathname: string): string[] {
  return pathname.split("/").filter(Boolean);
}

// The listing renders on exactly /spaces and the four-segment
// /spaces/{all|smartList|list}/:listId/:id variants; deeper smartList paths
// (post_to_smart_list) are posting flows, not the listing (see RouterData.ts).
function isSpacesListing(pathname: string): boolean {
  const segments = segmentsOf(pathname);

  if (segments[0] !== "spaces") {
    return false;
  }

  return (
    segments.length === 1 ||
    (segments.length === 4 && LISTING_SPACE_SEGMENTS.includes(segments[1]))
  );
}

// sessionStorage can throw (e.g. blocked storage); the breadcrumb must never
// take the page chrome down over a remembered filter.
function readSpacesListingUrl(): string {
  try {
    return sessionStorage.getItem(SPACES_LISTING_URL_KEY) || "/spaces";
  } catch {
    return "/spaces";
  }
}

function saveSpacesListingUrl(url: string): void {
  try {
    sessionStorage.setItem(SPACES_LISTING_URL_KEY, url);
  } catch {
    // Filter memory is best-effort; the crumb falls back to /spaces.
  }
}

function forgetSpacesListingUrl(): void {
  try {
    sessionStorage.removeItem(SPACES_LISTING_URL_KEY);
  } catch {
    // Filter memory is best-effort; the crumb falls back to /spaces.
  }
}

const SPACE_SUBPAGES: Record<string, string> = {
  schedule: "Schedule",
  members: "Members",
};

// "Spaces › <space> › <sub-page>", shared by real and demo spaces: the only
// difference between them is which state holds the name and what the space
// root looks like.
function spaceCrumbs(
  spaceRoot: string,
  name: string | undefined,
  rest: string[],
): Crumb[] {
  const crumbs: Crumb[] = [
    { key: "listing", labelKey: "Spaces", to: readSpacesListingUrl() },
    { key: "entity", label: name, to: rest.length ? spaceRoot : undefined },
  ];
  const subPage = SPACE_SUBPAGES[rest[0]];

  if (subPage) {
    crumbs.push({ key: "subpage", labelKey: subPage });
  }

  return crumbs;
}

// "Groups › <group> › <channel> › <sub-page>"; channel routes are
// /groups/:id/channels/:teamId[/members].
function groupCrumbs(
  groupId: string,
  rest: string[],
  entities: SelectedEntities,
): Crumb[] {
  const crumbs: Crumb[] = [
    { key: "listing", labelKey: "Groups", to: "/groups" },
    {
      key: "entity",
      label: nameMatchingRoute(entities.group, groupId),
      to: rest.length ? `/groups/${groupId}` : undefined,
    },
  ];
  const channelId = rest[0] === "channels" ? rest[1] : undefined;

  if (!channelId) {
    if (rest[0] === "members") {
      crumbs.push({ key: "subpage", labelKey: "Members" });
    }

    return crumbs;
  }

  crumbs.push({
    key: "channel",
    label: nameMatchingRoute(entities.channel, channelId),
    to:
      rest.length > 2 ? `/groups/${groupId}/channels/${channelId}` : undefined,
  });

  if (rest[2] === "members") {
    // Matches the page's own tab label — a channel's member list is
    // "Channel Members", unlike the group/space "Members" pages.
    crumbs.push({ key: "subpage", labelKey: "Channel Members" });
  }

  return crumbs;
}

// "Lists › <list>". The list detail root is /lists/:id/spaces (see
// Segments/routes.ts); deeper routes (edit, add_members, spaces/edit,
// spaces/post) link back to it.
function listCrumbs(
  listId: string,
  rest: string[],
  entities: SelectedEntities,
): Crumb[] {
  const atListRoot =
    rest.length === 0 || (rest.length === 1 && rest[0] === "spaces");

  return [
    { key: "listing", labelKey: "Lists", to: "/lists" },
    {
      key: "entity",
      label: entities.list,
      to: atListRoot ? undefined : `/lists/${listId}/spaces`,
    },
  ];
}

const TEMPLATE_SUBPAGES: Record<string, string> = {
  history: "History",
  members: "Members",
  // Matches the page's own heading — the route is reached from the "Edit Name"
  // menu item, but TemplateSpaceEdit titles itself "Edit Template".
  edit: "Edit Template",
};

// "Templates › <template> › <sub-page>". The template detail root is
// /space_templates/:id/posts (see TemplateTimeline/routes.ts) — the Posts tab
// is the root, so it earns no sub-page crumb of its own; Reorder sits one
// level deeper at /posts/reorder.
function templateCrumbs(
  templateId: string,
  rest: string[],
  entities: SelectedEntities,
): Crumb[] {
  const atTemplateRoot =
    rest.length === 0 || (rest.length === 1 && rest[0] === "posts");
  const crumbs: Crumb[] = [
    { key: "listing", labelKey: "Templates", to: "/space_templates" },
    {
      key: "entity",
      label: nameMatchingRoute(entities.template, templateId),
      to: atTemplateRoot ? undefined : `/space_templates/${templateId}/posts`,
    },
  ];
  const subPage =
    rest[0] === "posts" && rest[1] === "reorder"
      ? "Reorder"
      : TEMPLATE_SUBPAGES[rest[0]];

  if (subPage) {
    crumbs.push({ key: "subpage", labelKey: subPage });
  }

  return crumbs;
}

// Builds "Listing › Entity › Sub-page" crumbs for Spaces/Groups/Lists/
// Templates detail routes. Entity names come from state the detail pages
// already fetch, so an entity crumb only appears once that fetch lands;
// statically-labeled crumbs (listing, sub-page) render regardless, but the
// trail is cut at the first crumb whose name is still missing to avoid gaps
// like "Spaces › Members".
// On sub-routes without a named crumb (settings, edit flows, …) the entity
// crumb still links back to the entity root rather than posing as the
// current page.
function buildCrumbs(
  pathname: string,
  entities: SelectedEntities,
): Crumb[] | null {
  const [section, id, ...rest] = segmentsOf(pathname);

  if (!id) {
    return null;
  }

  if (section === "spaces" && id === "demo") {
    const [demoId, ...demoRest] = rest;

    return demoId
      ? spaceCrumbs(
          `/spaces/demo/${demoId}`,
          nameMatchingRoute(entities.demoSpace, demoId),
          demoRest,
        )
      : null;
  }

  if (section === "spaces" && !RESERVED_SPACE_SEGMENTS.includes(id)) {
    return spaceCrumbs(
      `/spaces/${id}`,
      nameMatchingRoute(entities.space, id),
      rest,
    );
  }

  if (section === "groups") {
    return groupCrumbs(id, rest, entities);
  }

  if (section === "lists") {
    return listCrumbs(id, rest, entities);
  }

  if (section === "space_templates") {
    return templateCrumbs(id, rest, entities);
  }

  return null;
}

const Breadcrumb = () => {
  const { pathname, search } = useLocation();
  const { t } = useTranslation();
  const {
    userKind,
    spaceId,
    spaceName,
    demoSpaceId,
    demoSpaceName,
    groupId,
    groupName,
    channelId,
    channelName,
    listName,
    templateId,
    templateName,
  } = useSelector(
    (state: RootState) => ({
      userKind: state.user.self?.kind,
      spaceId: state.space.selectedSpace?.id,
      spaceName: state.space.selectedSpace?.athleteName,
      // The demo space payload nests the space under .space (see
      // parseDemoSpace); DemoSpaceModel's own athleteName is never populated,
      // and the route id is the top-level demoSpaceId.
      demoSpaceId: state.demoSpace.selectedDemoSpace?.demoSpaceId,
      demoSpaceName: state.demoSpace.selectedDemoSpace?.space?.athleteName,
      groupId: state.groups.selectedGroup?.id,
      groupName: state.groups.selectedGroup?.name,
      channelId: state.teamGroup.selectedTeam?.id,
      channelName: state.teamGroup.selectedTeam?.name,
      listName: state.segments.segmentsData?.name,
      templateId: state.spaceTemplate.selectedTemplate?.id,
      templateName: state.spaceTemplate.selectedTemplate?.name,
    }),
    shallowEqual,
  );

  // The Spaces listing keeps its filter in the URL, so remembering the last
  // listing URL is what brings the filter back on the Spaces crumb. Leaving
  // the section altogether (sidebar → Lists → open a space) abandons that
  // filter, so forget it then instead of resurrecting it on the next space
  // detail page. The memory only feeds the coach-only crumbs below, so don't
  // touch it for athletes.
  useEffect(() => {
    if (userKind !== "coach") {
      return;
    }

    if (isSpacesListing(pathname)) {
      saveSpacesListingUrl(pathname + search);
    } else if (segmentsOf(pathname)[0] !== "spaces") {
      forgetSpacesListingUrl();
    }
  }, [userKind, pathname, search]);

  // Only coaches should be offered a way back to the Spaces/Groups/Lists
  // listings — those routes aren't athletes' to browse.
  if (userKind !== "coach") {
    return null;
  }

  const crumbs = buildCrumbs(pathname, {
    space: { id: spaceId, name: spaceName },
    demoSpace: { id: demoSpaceId, name: demoSpaceName },
    group: { id: groupId, name: groupName },
    channel: { id: channelId, name: channelName },
    list: listName,
    template: { id: templateId, name: templateName },
  });

  if (!crumbs) {
    return null;
  }

  const missingAt = crumbs.findIndex(
    (crumb) => !crumb.label && !crumb.labelKey,
  );
  const visible = missingAt === -1 ? crumbs : crumbs.slice(0, missingAt);

  if (!visible.length) {
    return null;
  }

  return (
    <nav
      aria-label={t("Breadcrumb")}
      data-testid="breadcrumb"
      className={styles.breadcrumb}
    >
      <ol>
        {visible.map((crumb, index) => {
          const text = crumb.labelKey ? t(crumb.labelKey) : crumb.label;

          return (
            <li key={crumb.key}>
              {index > 0 && (
                <i className="ico ico-chevron" aria-hidden="true" />
              )}
              {crumb.to ? (
                <Link to={crumb.to}>{text}</Link>
              ) : (
                <span aria-current="page">{text}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default memo(Breadcrumb);
